import { Instagram, Quote, Star } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Reveal, SectionHeading } from "@/components/site/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export function Testimonials() {
  const { content } = useSite();
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28" data-testid="testimonials-section">
      <SectionHeading eyebrow="Members" title={<>Real people.<br />Real reps.</>} />
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {content.testimonials.map((t, i) => (
          <Reveal key={t.name + i} delay={i * 0.1} className={i === 1 ? "md:translate-y-10" : ""}>
            <figure className="h-full rounded-md border border-white/10 bg-[#121212] p-7" data-testid={`testimonial-${i}`}>
              <Quote className="h-8 w-8 text-[var(--gold)]" />
              <blockquote className="mt-4 text-base leading-relaxed text-white/85">"{t.quote}"</blockquote>
              <figcaption className="mt-6 border-t border-white/10 pt-4">
                <p className="font-display text-lg font-bold uppercase text-white">{t.name}</p>
                <p className="font-mono-ui text-[11px] uppercase tracking-wider text-[var(--gold)]">{t.detail}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const Stars = ({ n }) => (
  <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= n ? "fill-[var(--gold)] text-[var(--gold)]" : "text-white/20"}`} />)}</div>
);

export function Reviews() {
  const { content } = useSite();
  const reviews = content.reviews;
  const avg = reviews.length ? reviews.reduce((a, r) => a + Number(r.rating), 0) / reviews.length : 0;
  return (
    <section className="border-y border-white/10 bg-[#0E0E0E]" data-testid="reviews-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-8 lg:grid-cols-[1fr_2fr]">
        <Reveal className="space-y-4">
          <p className="eyebrow">Google Reviews</p>
          <p className="h-display text-8xl text-white" data-testid="reviews-average">{avg.toFixed(1)}</p>
          <Stars n={Math.round(avg)} />
          <p className="text-sm text-white/55">Based on {reviews.length} recent reviews shown here.</p>
          <a href="https://www.google.com/maps/search/Iron+Pump+Gym+Queens+NY" target="_blank" rel="noopener noreferrer" className="btn-ghost !text-base" data-testid="reviews-google-link">Read on Google</a>
        </Reveal>
        <div className="grid gap-4 sm:grid-cols-2">
          {reviews.map((r, i) => (
            <Reveal key={r.name + i} delay={i * 0.06}>
              <div className="h-full rounded-md border border-white/10 bg-black/40 p-5" data-testid={`review-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--gold)]/15 font-display font-bold text-[var(--gold)]">{r.name[0]}</span>
                    <div><p className="text-sm font-semibold text-white">{r.name}</p><p className="text-xs text-white/40">{r.when}</p></div>
                  </div>
                  <Stars n={Number(r.rating)} />
                </div>
                <p className="mt-4 text-sm leading-relaxed text-white/70">{r.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function InstagramFeed() {
  const { content } = useSite();
  const handle = content.settings.instagram;
  const url = `https://www.instagram.com/${handle}/`;
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28" data-testid="instagram-section">
      <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
        <SectionHeading eyebrow="On the gram" title={`@${handle}`} />
        <a href={url} target="_blank" rel="noopener noreferrer" className="btn-gold !text-base" data-testid="instagram-follow-btn"><Instagram className="h-5 w-5" /> Follow</a>
      </div>
      <div className="mt-10 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3 lg:grid-cols-6">
        {content.instagram.map((p, i) => (
          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="group relative aspect-square overflow-hidden rounded-sm" data-testid={`instagram-post-${i}`}>
            <img src={p.image} alt={p.caption} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
            <div className="absolute inset-0 flex items-end bg-black/70 p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <p className="text-xs text-white">{p.caption}</p>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

export function FaqList() {
  const { content } = useSite();
  return (
    <Accordion type="single" collapsible className="w-full" data-testid="faq-list">
      {content.faqs.map((f, i) => (
        <AccordionItem key={i} value={`f${i}`} className="border-white/10">
          <AccordionTrigger className="py-5 text-left font-display text-xl font-bold uppercase tracking-wide text-white hover:text-[var(--gold)] hover:no-underline" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
          <AccordionContent className="pb-5 text-sm leading-relaxed text-white/65 sm:text-base">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
